<template>
  <div>
    <EsNotificationDefaultSettings
        :locationMode="defaultSettings.locationMode"
        :locations="isCorporate ? allLocations : assignedLocations"
        :selectedLocations="defaultSettings.selectedLocations"
        :selectedRequestPriorities="defaultSettings.requestPriorities"
        :isCorporate="isCorporate"
        :userId="userId"
        :isGlobalDefaults="true"
        @location-mode-change="($event) => setDefaultSettingsValue('locationMode', $event)"
        @selected-locations-change="($event) => setDefaultSettingsValue('selectedLocations', $event)"
        @request-priorities-change="($event) => setDefaultSettingsValue('requestPriorities', $event)"
    />
    <v-divider class="my-5"></v-divider>
    <v-row>
      <v-col cols="4">
        <span class="font-weight-bold">Notifications</span>
      </v-col>
      <v-col
        cols="2"
        v-for="channel in notificationChannels"
        :key="channel.name">
        {{ channel.label }}
      </v-col>
      <v-col cols="2">Use Default Settings</v-col>
      <v-col cols="3"></v-col>
    </v-row>
    <div
      v-for="notificationType in notificationTypes"
      :key="notificationType.id">
      <v-tooltip
        top
        max-width="300px"
        :disabled="notificationIsApplicableForUser(notificationType)">
        <template v-slot:activator="{ on }">
          <div v-on="on">
            <div
              :class="{
                'notification-disabled':
                  !notificationIsApplicableForUser(notificationType),
              }">
              <v-row>
                <v-col cols="4">
                  <v-tooltip top max-width="300px">
                    <template v-slot:activator="{ on }">
                      <div v-on="on" style="display: inline-block">
                        <span>{{ notificationType.name }}</span>
                        <v-icon small>mdi-help-circle-outline</v-icon>
                      </div>
                    </template>
                    <span>
                      {{ notificationType.description }}
                    </span>
                  </v-tooltip>
                </v-col>
                <v-col
                  cols="2"
                  v-for="channel in notificationChannels"
                  :key="channel.name">
                  <v-tooltip
                    top
                    max-width="300px"
                    :disabled="
                      notificationType.channels.includes(channel.name)
                    ">
                    <template v-slot:activator="{ on }">
                      <div v-on="on">
                        <v-checkbox
                          v-if="
                            !notificationIsApplicableForUser(notificationType)
                          "
                          class="mt-0"
                          hide-details
                          disabled
                          indeterminate></v-checkbox>
                        <v-checkbox
                          v-if="
                            notificationIsApplicableForUser(notificationType)
                          "
                          class="mt-0"
                          hide-details
                          v-model="
                            notificationPreferenceMap[notificationType.id][
                              channel.name
                            ]
                          "
                          @change="
                            setNotificationPreferenceValue(
                              notificationType.id,
                              channel.name,
                              !!$event,
                            )
                          "
                          :disabled="
                            !notificationType.channels.includes(channel.name)
                          "
                          :indeterminate="
                            !notificationType.channels.includes(channel.name)
                          "></v-checkbox>
                      </div>
                    </template>
                    <span>
                      This notification type does not support
                      {{ channel.label }} at this time.
                    </span>
                  </v-tooltip>
                </v-col>
                <v-col cols="2">
                  <v-checkbox
                    v-if="!notificationIsApplicableForUser(notificationType)"
                    class="mt-0"
                    hide-details
                    disabled
                    indeterminate></v-checkbox>
                  <v-checkbox
                    v-if="notificationIsApplicableForUser(notificationType)"
                    class="mt-0"
                    hide-details
                    v-model="
                      notificationPreferenceMap[notificationType.id]
                        .useDefaultSettings
                    "
                    @change="
                      setNotificationPreferenceValue(
                        notificationType.id,
                        'useDefaultSettings',
                        !!$event,
                      )
                    "></v-checkbox>
                </v-col>
              </v-row>

                <EsNotificationDefaultSettings
                    v-if="
                      notificationIsApplicableForUser(notificationType) &&
                      !getNotificationPreferenceValue(
                        notificationType.id,
                        'useDefaultSettings',
                      )
                    "
                    :locationMode="notificationPreferenceMap[notificationType.id]
                          .locationMode"
                    :locations="isCorporate ? allLocations : assignedLocations"
                    :selectedLocations="getNotificationPreferenceValue(notificationType.id, 'selectedLocations', [])"
                    :selectedRequestPriorities="getNotificationPreferenceValue(notificationType.id, 'requestPriorities', [])"
                    :isCorporate="isCorporate"
                    :userId="userId"
                    :notificationTypeId="notificationType.id"
                    @location-mode-change="($event) => setNotificationPreferenceValue(notificationType.id, 'locationMode', $event)"
                    @selected-locations-change="($event) => setNotificationPreferenceValue(notificationType.id, 'selectedLocations', $event)"
                    @request-priorities-change="($event) => setNotificationPreferenceValue(notificationType.id, 'requestPriorities', $event)"
                />
              <v-divider class="my-3"></v-divider>
            </div>
          </div>
        </template>
        <span>
          {{ getNotificationApplicabilityReason(notificationType) }}
        </span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import EsNotificationDefaultSettings from './EsNotificationDefaultSettings.vue';


export default {
  name: 'EsNotificationPreferences',
  components: {
    EsNotificationDefaultSettings
  },
  props: {
    allLocations: {
      type: Array,
      required: true,
    },
    notificationTypes: {
        type: Array,
        required: true,
    },
    initialNotificationPreferences: {
      type: Array,
      required: false,
    },
    initialDefaultSettings: {
      type: Object,
      required: false,
    },
    userId: {
      type: String,
      required: false,
    },
    isCorporate: {
      type: Boolean,
      required: false,
      default: false,
    },
    isInternalTech: {
      type: Boolean,
      required: false,
      default: false,
    },
    assignedLocationIds: {
      type: Array,
      required: false,
      default: () => [],
    },
    app: {
      type: String,
      default: 'ssp',
    }
  },
  data: () => ({
    /**
     * @type {Array<{ id: string; name: string; description: string; channels: string[] }>}
     */
    notificationChannels: [
      {
        name: 'push',
        label: 'Push',
      },
      {
        name: 'email',
        label: 'Email',
      },
      {
        name: 'sms',
        label: 'Text',
      },
    ],
    notificationPreferences: [],
    defaultSettings: {
      locationMode: 'assigned',
      selectedLocations: [],
      requestPriorities: [],
    },
    notificationPreferenceMap: {},
  }),
  watch: {
    initialNotificationPreferences: {
      handler() {
        this.setInitials();
      },
      immediate: true,
    },
    initialDefaultSettings: {
      handler() {
        this.setInitials();
      },
      immediate: true,
    },
    notificationTypes: {
      handler() {
        this.setInitials();
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    assignedLocations() {
      return this.allLocations.filter((loc) =>
        this.assignedLocationIds.includes(loc.id),
      );
    },
  },
  methods: {
    setInitials() {
      if (this.initialNotificationPreferences) {
        this.notificationPreferences = this.initialNotificationPreferences;
      }

      if (this.initialDefaultSettings) {
        this.defaultSettings = this.initialDefaultSettings;
      }

      this.notificationPreferenceMap = this.notificationTypes.reduce(
        (acc, type) => {
          acc[type.id] =
            this.notificationPreferences.find(
              (pref) => pref.notificationType === type.id,
            ) ?? {};
          return acc;
        },
        {},
      );
    },
    setDefaultSettingsValue(key, value) {
      this.defaultSettings[key] = value;
      this.$forceUpdate();
      this.$emit('default-settings-updated', this.defaultSettings);
    },
    getNotificationPreferenceValue(notificationTypeId, key, defaultValue) {
      const pref = this.notificationPreferences.find(
        (p) => p.notificationType === notificationTypeId,
      );
      return pref?.[key] ?? defaultValue;
    },
    setNotificationPreferenceValue(notificationTypeId, key, value) {
      const pref = this.notificationPreferences.find(
        (p) => p.notificationType === notificationTypeId,
      );
      if (pref) {
        pref[key] = value;
        this.$forceUpdate();
        this.$emit('preferences-updated', this.notificationPreferences);
      }
    },
    getNotificationApplicabilityDetails(notificationType) {
      if (
        notificationType?.userConditions?.internalTech &&
        !this.isInternalTech
      ) {
        return {
          applicable: false,
          reason:
            `This notification is only applicable for users who are internal techs.${this.app === 'ssp' ? ' Please contact your account manager for more information.' :''}`,
        };
      }
      return {
        applicable: true,
      };
    },
    notificationIsApplicableForUser(notificationType) {
      return this.getNotificationApplicabilityDetails(notificationType)
        .applicable;
    },
    getNotificationApplicabilityReason(notificationType) {
      return this.getNotificationApplicabilityDetails(notificationType).reason;
    },
  },
};
</script>

<style scoped>
.notification-disabled {
  opacity: 0.5;
  pointer-events: none;
}
</style>
